import React, { JSX, startTransition, useContext } from "react";
import {
    Action,
    activeEditor$,
    addActivePlugin$,
    addComposerChild$,
    addExportVisitor$,
    addImportVisitor$,
    addLexicalNode$,
    Cell,
    createActiveEditorSubscription$,
    createRootEditorSubscription$,
    getValue,
    map,
    Realm,
    realmPlugin,
    rootEditor$,
    useCellValue,
    withLatestFrom,
} from "@mdxeditor/editor";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
    LexicalTypeaheadMenuPlugin,
    MenuOption,
    MenuTextMatch,
    useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import {
    $createTextNode,
    COMMAND_PRIORITY_HIGH,
    COMMAND_PRIORITY_LOW,
    COMMAND_PRIORITY_NORMAL,
    KEY_DOWN_COMMAND,
    TextNode,
    $getSelection,
    SELECTION_CHANGE_COMMAND,
    RangeSelection,
    getDOMTextNode,
    ElementNode,
    $getCaretRange,
    $caretRangeFromSelection,
} from "lexical";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as ReactDOM from "react-dom";

import { $createArtifactNode, ArtifactNode } from "./ArtifactNode";
import { adminGetArtifacts } from "@/api";
import { artifact_list_item } from "@/app/typing/artifacts";
import { observer } from "mobx-react-lite";
import { appState } from "@/app/appState";

const PUNCTUATION = "\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%'\"~=<>_:;";
const NAME = "\\b[A-Z][^\\s" + PUNCTUATION + "]";

const DocumentArtifactsRegex = {
    NAME,
    PUNCTUATION,
};

const PUNC = DocumentArtifactsRegex.PUNCTUATION;

const TRIGGERS = "${"; // No need for array and join for a simple string

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = "[^" + TRIGGERS.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + PUNC + "\\s]";

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
    "(?:" +
    "\\.[ |$]|" + // E.g. "r. " in "Mr. Smith"
    " |" + // E.g. " " in "Josh Duck"
    "[" +
    PUNC +
    "]|" + // E.g. "-' in "Salier-Hellendag"
    ")";

const LENGTH_LIMIT = 75;

const AtSignArtifactsRegex = new RegExp(
    "(^|\\s|\\()(" +
    TRIGGERS.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") +
    "((?:" +
    VALID_CHARS +
    VALID_JOINS +
    "){0," +
    LENGTH_LIMIT +
    "})" +
    ")$"
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignArtifactsRegexAliasRegex = new RegExp(
    "(^|\\s|\\()(" + TRIGGERS.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "((?:" + VALID_CHARS + "){0," + ALIAS_LENGTH_LIMIT + "})" + ")$"
);

// At most, 5 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 5;

const artifactsCache = new Map();

const lookupService = {
    search(data: readonly string[], string: string, callback: (results: Array<string>) => void): void {
        setTimeout(() => {
            const results = data.filter((mention) => mention.toLowerCase().includes(string.toLowerCase()));
            callback(results);
        }, 500);
    },
};

function useArtifactLookupService(data: readonly string[], mentionString: string | null) {
    const [results, setResults] = useState<Array<string>>([]);

    useEffect(() => {
        const cachedResults = artifactsCache.get(mentionString);

        if (mentionString == null) {
            setResults([]);
            return;
        }

        if (cachedResults === null) {
            return;
        } else if (cachedResults !== undefined) {
            setResults(cachedResults);
            return;
        }

        artifactsCache.set(mentionString, null);
        lookupService.search(data, mentionString, (newResults) => {
            artifactsCache.set(mentionString, newResults);
            setResults(newResults);
        });
    }, [mentionString]);

    return results;
}

function checkForAtSignArtifacts(text: string, minMatchLength: number): MenuTextMatch | null {
    let match = AtSignArtifactsRegex.exec(text);

    if (match === null) {
        match = AtSignArtifactsRegexAliasRegex.exec(text);
    }
    if (match !== null) {
        // The strategy ignores leading whitespace but we need to know it's
        // length to add it to the leadOffset
        const maybeLeadingWhitespace = match[1];

        const matchingString = match[3];
        if (matchingString.length >= minMatchLength) {
            return {
                leadOffset: match.index + maybeLeadingWhitespace.length,
                matchingString,
                replaceableString: match[2],
            };
        }
    }
    return null;
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
    return checkForAtSignArtifacts(text, 0);
}

class MentionTypeaheadOption extends MenuOption {
    name: string;

    constructor(name: string) {
        super(name);
        this.name = name;
    }
}

function ArtifactsTypeaheadMenuItem({ index, onClick, option }: { index: number; onClick: () => void; option: MentionTypeaheadOption }) {
    return (
        <li
            key={option.key}
            tabIndex={-1}
            className="typeahead-item"
            ref={option.setRefElement}
            role="option"
            id={"typeahead-item-" + index}
            onClick={onClick}
        >
            <p>{option.name}</p>
        </li>
    );
}

// The actualt component, this is where render content goes
const Typehead = observer(function Typehead() {
    const editor = useCellValue(rootEditor$);
    const [queryString, setQueryString] = useState<string | null>(null);

    const id = 2;

    const [data, setData] = useState([""]);

    useEffect(() => {
        setData(appState.artifacts?.filter((y) => y.id != id).map((x) => x.variable_name) ?? []);
    }, [appState.artifacts]);

    const results = useArtifactLookupService(data, queryString);

    const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch("/", {
        minLength: 0,
    });

    if (!editor) return;

    const options = useMemo(
        () => results.map((result) => new MentionTypeaheadOption(result)).slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
        [results]
    );



    const onSelectOption = useCallback(
        (selectedOption: MentionTypeaheadOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
            editor.update(() => {
                // Construct the full text with trigger, name, and suffix
                const fullText = "${" + selectedOption.name + ', ""}';

                const textNode = $createTextNode(fullText);

                if (nodeToReplace) {
                    nodeToReplace.replace(textNode);
                }

                textNode.select();
                closeMenu();
            });
        },
        [editor]
    );

    const checkForMentionMatch = useCallback(
        (text: string) => {
            const slashMatch = checkForSlashTriggerMatch(text, editor);
            if (slashMatch !== null) {
                return null;
            }

            const queryMatch = getPossibleQueryMatch(text)



            return queryMatch;
        },
        [checkForSlashTriggerMatch, editor]
    );

    return (
        <div>
            <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
                onQueryChange={setQueryString}
                onSelectOption={onSelectOption}
                triggerFn={checkForMentionMatch}
                options={options}
                menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp }) => {

                    if (!anchorElementRef.current || !results.length) return null;


                    // Get the anchor's position
                    const rect = anchorElementRef.current.getBoundingClientRect();

                    return ReactDOM.createPortal(
                        <div
                            className="typeahead"
                            style={{
                                top: rect.bottom + window.scrollY,
                                left: rect.left + window.scrollX,
                            }}
                        >
                            <ul>
                                {options.map((option, i: number) => (
                                    <ArtifactsTypeaheadMenuItem
                                        index={i}
                                        onClick={() => selectOptionAndCleanUp(option)}
                                        key={option.key}
                                        option={option}
                                    />
                                ))}
                            </ul>
                        </div>,
                        document.body  // ← Portal to body instead of anchor
                    );
                }}
            />
        </div>
    );
});
// Mounts the Typehead on the editor via the MDX editor plugin system
export const artifactsPlugin = realmPlugin<{}>({
    init(realm, params) {
        realm.pubIn({
            [addActivePlugin$]: "typehead",
            [addComposerChild$]: Typehead,
            //[addLexicalNode$]: ArtifactNode,
        });
    },
    update(realm, params) { },
});

function CaretScroll() {
    const [editor] = useLexicalComposerContext();

    const funq = (e: Event) => {
        if (!(e.target instanceof Document)) return;

        try {
            const selection = getSelection();
            const range = selection?.getRangeAt(0);

            if (!range || !selection) return;

            // Get the editor's root DOM element
            const editorElement = editor.getRootElement();
            if (!editorElement) return;

            // Check if the selection is within this editor instance
            if (!editorElement.contains(range.commonAncestorContainer)) return;

            const x = range.getClientRects()[0];
            if (!x) return;

            if (x.top < 200) window.scrollBy({ top: -50 });
            else if (window.innerHeight - x.bottom < 30) window.scrollBy({ top: 30 });
        } catch { }
    };

    useEffect(() => {
        document.addEventListener("selectionchange", funq);

        return () => {
            document.removeEventListener("selectionchange", funq);
        };
    }, [editor]);

    return <div></div>;
}
export const caretScrollPlugin = realmPlugin<{}>({
    init(realm, params) {
        realm.pubIn({
            [addActivePlugin$]: "caretScroll",
            [addComposerChild$]: CaretScroll,
            //[addLexicalNode$]: ArtifactNode,
        });
    },
    update(realm, params) { },
});
